/**
 * Orders adapter — bridges the Emergent Checkout's `POST /api/orders` shape
 * to DyntraStore's `POST /v1/checkout/place-order`.
 *
 * The Emergent UI posts { items, address, phone_verified, payment_method }
 * and expects a response like { order_no }. NestJS's place-order flow needs a
 * saved addressId and reads items from the server-side cart, so we:
 *   1. push the shipping address to /v1/me/addresses (best-effort),
 *   2. call /v1/checkout/place-order with the returned addressId,
 *   3. surface `order_no` from the returned order.
 */
import { api } from "@/lib/api";

function toPaymentMethod(v) {
  const s = (v || "cod").toLowerCase();
  if (s === "cod") return "COD";
  if (s === "upi") return "UPI";
  if (s === "card") return "CARD";
  if (s === "wallet") return "WALLET";
  return "COD";
}

async function saveAddress(a) {
  const payload = {
    label: a.full_name || "Home",
    line1: a.line1,
    line2: a.line2 || undefined,
    city: a.city,
    state: a.state,
    pincode: String(a.pincode || "").replace(/\D/g, "").slice(0, 6),
    phone: a.phone,
  };
  try {
    const { data } = await api.post("/addresses", payload);
    return data?.id || data?.address?.id || null;
  } catch {
    return null;
  }
}

/**
 * Place an order using the currently-authenticated user's server-side cart.
 * `payload.items` is ignored (NestJS reads from the server cart) but kept in
 * the signature for parity with the original Emergent contract.
 */
export async function createOrder(payload) {
  const addressId = await saveAddress(payload.address || {});
  const body = {
    addressId,
    paymentMethod: toPaymentMethod(payload.payment_method),
    couponCode: payload.coupon_code || undefined,
    walletApply: Boolean(payload.wallet_apply),
    freebieOfferIds: payload.freebie_offer_ids || [],
  };

  const { data } = await api.post("/checkout/place-order", body);
  return {
    order_no: data?.orderNumber || data?.order?.number || data?.number || data?.orderId,
    order: data,
    payment: data?.paymentInit || null,
  };
}
